import { atom } from "jotai";

import { Logger } from "@moment/logging";

import { $accessTokenFunc } from "../authenication";
import { $docsApi } from "../docsapi";
import { MetadataLoadError } from "../document-metadata/errors";
import {
	$loadDocumentMetadata,
	$setMetadataStateError,
} from "../document-metadata/metadata-state.atom";

const logger = new Logger("load-template-metadata");

const TEMPLATE_WORKSPACE = "moment-templates";

export interface LoadTemplateMetadataInput {
	key: string;
	templateId: string;
}

/**
 * Loads document metadata for a template from the DocsAPI.
 * Templates are loaded in read-only SSR mode (no persistence callbacks).
 */
export const $loadTemplateMetadata = atom(
	null,
	async (get, set, input: LoadTemplateMetadataInput) => {
		const { key, templateId } = input;

		logger.info("Loading template metadata", { key, templateId });

		try {
			const { getAccessTokenSilently } = get($accessTokenFunc);
			const token = await getAccessTokenSilently?.();
			logger.debug("Got access token", { hasToken: !!token });

			logger.debug("Fetching metadata from DocsAPI", {
				workspaceName: TEMPLATE_WORKSPACE,
				documentId: templateId,
			});
			const metadata = await get($docsApi).document.metadata.read(
				{ workspaceName: TEMPLATE_WORKSPACE, documentId: templateId },
				token
			);

			if (!metadata) {
				throw new Error("Template metadata not found");
			}

			logger.debug("Got metadata, loading into state", { key, title: metadata.title });
			// Load as SSR/read-only (no persistence callbacks needed)
			set($loadDocumentMetadata, { key, metadata, ssr: true });
			logger.info("Loaded template metadata successfully", { templateId });
		} catch (err) {
			const error = new MetadataLoadError(err);
			logger.error("Failed to load template metadata", { templateId, err });
			set($setMetadataStateError, { key, error });
		}
	}
);
$loadTemplateMetadata.debugLabel = "loadTemplateMetadata";
